import { ok } from 'neverthrow';
import type {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { WorkflowEditorToolbarActionBase } from './workflow-editor-toolbar-action.base';
import {
  workflowCanvasHasNodeSelection,
  workflowCanvasShortcutAvailable,
  workflowModelFromContext,
  type WorkflowClipboardOps,
} from './workflow-toolbar-action.helpers';
import {
  pasteWorkflowSlice,
  workflowSelectionPayload,
} from './workflow-node-clipboard';

/**
 * Copies the selected nodes and pastes them straight back in, leaving the
 * clipboard alone.
 *
 * Each copy lands beside its original, fed by the same upstream nodes.
 * Positions aren't persisted — dagre lays the canvas out from the edges — so
 * sharing the original's inputs is what "next to it" means here. In a graph
 * where those inputs are the ones missing (there are none, or the selection is
 * an entry node) the copies land loose, which the toast says.
 */
export class WorkflowToolbarDuplicateSelectionAction extends WorkflowEditorToolbarActionBase {
  title = 'Duplicate';
  override description = 'Duplicate the selected nodes';
  group = 'Workflow';
  icon = 'copy';
  shortcut = ['ctrl', 'd'];

  constructor(private readonly ops: WorkflowClipboardOps) {
    super();
  }

  /**
   * No text-selection check, unlike copy: the browser's own Cmd-D (bookmark
   * this page) is the one shortcut here worth overriding outright — nothing on
   * this page wants it.
   */
  shortcutAvailable(): boolean {
    return workflowCanvasShortcutAvailable(this.ops);
  }

  /**
   * Nodes only — a trigger binds a row that lives outside the workflow, so
   * there is nothing here to duplicate for one.
   */
  override applicable(context: ApplicableContext): boolean {
    return workflowCanvasHasNodeSelection(context);
  }

  override async execute(
    _params: Record<string, never>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    const model = workflowModelFromContext(context);
    const payload = model === null ? null : workflowSelectionPayload(model);
    if (model === null || payload === null) {
      return ok({});
    }
    return pasteWorkflowSlice({
      model,
      payload,
      anchor: { kind: 'source-inputs' },
      verb: 'Duplicated',
    });
  }
}
